import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import { Award, Search, Plus, Minus, Save, Gift, Trash2, Edit2 } from 'lucide-react';

const Fidelite = () => {
  const { toasts, toast, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [niveaux, setNiveaux] = useState([]);
  const [niveauxOriginaux, setNiveauxOriginaux] = useState([]); // Pour comparer les modifications
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Section Avantages Clickorico
  const [avantagesClickorico, setAvantagesClickorico] = useState([]);
  const [loadingAvantages, setLoadingAvantages] = useState(false);
  const [editingAvantage, setEditingAvantage] = useState(null);
  const [showAvantageForm, setShowAvantageForm] = useState(false);
  const [avantageForm, setAvantageForm] = useState({
    nom: '',
    description: '',
    type: 'REDUCTION_POURCENT',
    actif: false,
    ordre: 0,
    valeur: null,
    statutMinimum: 'BRONZE',
    dateDebut: null,
    dateFin: null,
    prisEnChargeParClickorico: true,
    coutClickorico: null
  });

  // Section Service Client
  const [searchTerm, setSearchTerm] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [pointsAjustement, setPointsAjustement] = useState('');
  const [raisonAjustement, setRaisonAjustement] = useState('');

  useEffect(() => {
    loadNiveaux();
    loadAvantagesClickorico();
  }, []);

  const loadNiveaux = async () => {
    try {
      // Ne pas afficher le loading si on recharge juste après une mise à jour
      if (!saving) {
        setLoading(true);
      }
      const response = await apiService.getNiveauxCarteFidelite();
      const niveauxData = response || [];
      setNiveaux(niveauxData);
      setNiveauxOriginaux(JSON.parse(JSON.stringify(niveauxData))); // Copie profonde pour comparaison
      setHasUnsavedChanges(false);
      
      // Si aucun niveau n'est retourné, afficher un message informatif
      if (niveauxData.length === 0) {
        toast.error('Aucun niveau de fidélité trouvé. La migration Prisma doit être appliquée.', { duration: 5000 });
      }
    } catch (error) {
      console.error('Erreur chargement niveaux:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Impossible de charger les niveaux de fidélité';
      const errorDetails = error?.response?.data?.details || '';
      const errorCode = error?.response?.data?.code;
      
      if (errorCode === 'MIGRATION_REQUIRED') {
        toast.error('⚠️ La migration Prisma doit être appliquée. Veuillez exécuter : npx prisma migrate deploy', { duration: 7000 });
      } else {
        toast.error(errorMessage + (errorDetails ? ` : ${errorDetails}` : ''), { duration: 5000 });
      }
    } finally {
      if (!saving) {
        setLoading(false);
      }
    }
  };

  // Vérifier si des modifications ont été apportées
  const checkForChanges = (updatedNiveaux) => {
    const hasChanges = updatedNiveaux.some((niveau, index) => {
      const original = niveauxOriginaux[index];
      if (!original) return false;
      return niveau.pointsMin !== original.pointsMin || niveau.pointsMax !== original.pointsMax;
    });
    setHasUnsavedChanges(hasChanges);
  };

  // Sauvegarder toutes les modifications
  const saveAllChanges = async () => {
    if (!hasUnsavedChanges) {
      toast.info('Aucune modification à sauvegarder');
      return;
    }

    // Validation avant sauvegarde
    const validationErrors = [];
    for (let i = 0; i < niveaux.length; i++) {
      const niveau = niveaux[i];
      const original = niveauxOriginaux[i];
      
      if (!original) continue;

      // Vérifier si ce niveau a été modifié
      const hasChanges = niveau.pointsMin !== original.pointsMin || niveau.pointsMax !== original.pointsMax;
      if (!hasChanges) continue;

      // Validation : pointsMax doit être supérieur à pointsMin (si pointsMax n'est pas null)
      if (niveau.pointsMax !== null && niveau.pointsMax <= niveau.pointsMin) {
        validationErrors.push({
          niveau: niveau.nom,
          message: `Le pointsMax (${niveau.pointsMax}) doit être supérieur à pointsMin (${niveau.pointsMin})`
        });
      }

      // Validation : pointsMin doit être >= 0
      if (niveau.pointsMin < 0) {
        validationErrors.push({
          niveau: niveau.nom,
          message: `Le pointsMin ne peut pas être négatif`
        });
      }

      // Validation : pointsMax doit être >= 0 (si défini)
      if (niveau.pointsMax !== null && niveau.pointsMax < 0) {
        validationErrors.push({
          niveau: niveau.nom,
          message: `Le pointsMax ne peut pas être négatif`
        });
      }
    }

    if (validationErrors.length > 0) {
      toast.error(`Erreurs de validation : ${validationErrors.map(e => `${e.niveau}: ${e.message}`).join(', ')}`, { duration: 7000 });
      return;
    }

    try {
      setSaving(true);
      const updates = [];

      // Comparer chaque niveau et sauvegarder les modifications
      for (let i = 0; i < niveaux.length; i++) {
        const niveau = niveaux[i];
        const original = niveauxOriginaux[i];
        
        if (!original) continue;

        const changes = {};
        if (niveau.pointsMin !== original.pointsMin) {
          changes.pointsMin = niveau.pointsMin;
        }
        if (niveau.pointsMax !== original.pointsMax) {
          changes.pointsMax = niveau.pointsMax;
        }

        if (Object.keys(changes).length > 0) {
          updates.push(
            apiService.updateNiveauCarteFidelite(niveau.id, changes)
              .then(() => ({ success: true, niveau: niveau.nom }))
              .catch(error => {
                console.error(`Erreur mise à jour niveau ${niveau.nom}:`, error);
                return { 
                  error: true, 
                  niveau: niveau.nom, 
                  message: error?.response?.data?.error || error?.message 
                };
              })
          );
        }
      }

      const results = await Promise.all(updates);
      
      // Vérifier s'il y a des erreurs
      const errors = results.filter(r => r && r.error);
      const successes = results.filter(r => r && r.success);
      
      if (errors.length > 0) {
        const errorMessages = errors.map(e => `${e.niveau}: ${e.message}`).join(', ');
        toast.error(`Erreurs lors de la sauvegarde : ${errorMessages}`, { duration: 7000 });
      }
      
      if (successes.length > 0) {
        if (errors.length === 0) {
          toast.success('Toutes les modifications ont été sauvegardées avec succès');
        } else {
          toast.success(`${successes.length} niveau(x) sauvegardé(s) avec succès`);
        }
        await loadNiveaux(); // Recharger pour avoir les valeurs à jour
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde des modifications');
    } finally {
      setSaving(false);
    }
  };

  // Cette fonction n'est plus utilisée directement, mais gardée pour référence
  const handleUpdateNiveau = async (niveauId, field, value) => {
    // Ne plus sauvegarder automatiquement
  };

  const searchClient = async () => {
    if (!searchTerm.trim()) {
      toast.error('Veuillez saisir un email ou nom de client');
      return;
    }

    try {
      const response = await apiService.searchUsers(searchTerm);
      // La réponse est { data: [...], total: ... } ou directement un tableau
      const users = response?.data || (Array.isArray(response) ? response : []);
      // Filtrer uniquement les clients (pas les commerçants ni admins)
      const clientsOnly = users.filter(user => 
        !user.role || 
        (user.role !== 'COMMERCANT' && user.role !== 'SUPER_ADMIN' && user.role !== 'ADMIN')
      );
      setClientResults(clientsOnly);
      
      if (clientsOnly.length === 0) {
        toast.error('Aucun client trouvé');
      }
    } catch (error) {
      console.error('Erreur recherche client:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Erreur lors de la recherche du client';
      toast.error(errorMessage);
    }
  };

  const loadAvantagesClickorico = async () => {
    try {
      setLoadingAvantages(true);
      const response = await apiService.getAvantagesClickorico();
      setAvantagesClickorico(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Erreur chargement avantages Clickorico:', error);
      toast.error('Impossible de charger les avantages Clickorico');
    } finally {
      setLoadingAvantages(false);
    }
  };

  const handleSaveAvantage = async () => {
    try {
      if (editingAvantage) {
        await apiService.updateAvantageClickorico(editingAvantage.id, avantageForm);
        toast.success('Avantage mis à jour avec succès');
      } else {
        await apiService.createAvantageClickorico(avantageForm);
        toast.success('Avantage créé avec succès');
      }
      setShowAvantageForm(false);
      setEditingAvantage(null);
      setAvantageForm({
        nom: '',
        description: '',
        type: 'REDUCTION_POURCENT',
        actif: false,
        ordre: 0,
        valeur: null,
        statutMinimum: 'BRONZE',
        dateDebut: null,
        dateFin: null,
        prisEnChargeParClickorico: true,
        coutClickorico: null
      });
      loadAvantagesClickorico();
    } catch (error) {
      console.error('Erreur sauvegarde avantage:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Erreur lors de la sauvegarde';
      toast.error(errorMessage);
    }
  };

  const handleDeleteAvantage = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet avantage ?')) {
      return;
    }

    try {
      await apiService.deleteAvantageClickorico(id);
      toast.success('Avantage supprimé avec succès');
      loadAvantagesClickorico();
    } catch (error) {
      console.error('Erreur suppression avantage:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Erreur lors de la suppression';
      toast.error(errorMessage);
    }
  };

  const handleEditAvantage = (avantage) => {
    setEditingAvantage(avantage);
    setAvantageForm({
      nom: avantage.nom,
      description: avantage.description || '',
      type: avantage.type,
      actif: avantage.actif,
      ordre: avantage.ordre || 0,
      valeur: avantage.valeur,
      statutMinimum: avantage.statutMinimum,
      dateDebut: avantage.dateDebut ? new Date(avantage.dateDebut).toISOString().split('T')[0] : null,
      dateFin: avantage.dateFin ? new Date(avantage.dateFin).toISOString().split('T')[0] : null,
      prisEnChargeParClickorico: avantage.prisEnChargeParClickorico !== false,
      coutClickorico: avantage.coutClickorico
    });
    setShowAvantageForm(true);
  };

  const ajusterPoints = async () => {
    if (!selectedClient || !pointsAjustement || !raisonAjustement.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      await apiService.ajusterPointsClient(
        selectedClient.id,
        parseInt(pointsAjustement),
        raisonAjustement
      );
      toast.success('Points ajustés avec succès');
      setSelectedClient(null);
      setPointsAjustement('');
      setRaisonAjustement('');
      setSearchTerm('');
      setClientResults([]);
    } catch (error) {
      console.error('Erreur ajustement points:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Erreur lors de l\'ajustement des points';
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast notifications */}
      {toasts.map((toastItem) => (
        <Toast key={toastItem.id} toast={toastItem} onClose={() => removeToast(toastItem.id)} />
      ))}

      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">🏆 Gestion Fidélité</h1>
        
        {/* Section 1: Gestion des niveaux */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">Niveaux de Carte de Fidélité</h2>
              <p className="text-sm text-gray-600 mt-1">
                Configurez les seuils de points pour chaque niveau. Ces seuils s'appliquent à tous les commerçants.
              </p>
            </div>
            {hasUnsavedChanges && (
              <button
                onClick={saveAllChanges}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            )}
          </div>
          
          <div className="space-y-4">
            {niveaux.map((niveau) => (
              <div key={niveau.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{niveau.icone || '🎯'}</div>
                    <div>
                      <h3 className="font-semibold">{niveau.nom}</h3>
                      <p className="text-sm text-gray-600">{niveau.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Points min</label>
                      <input
                        type="number"
                        min="0"
                        value={niveau.pointsMin}
                        onChange={(e) => {
                          // Mettre à jour uniquement l'affichage local, pas la base de données
                          const value = Math.max(0, parseInt(e.target.value) || 0);
                          const newNiveaux = niveaux.map(n => 
                            n.id === niveau.id ? { ...n, pointsMin: value } : n
                          );
                          setNiveaux(newNiveaux);
                          checkForChanges(newNiveaux);
                        }}
                        className="w-24 px-2 py-1 border rounded"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Points max</label>
                      <input
                        type="number"
                        min="0"
                        value={niveau.pointsMax || ''}
                        onChange={(e) => {
                          // Mettre à jour uniquement l'affichage local, pas la base de données
                          const value = e.target.value ? Math.max(0, parseInt(e.target.value)) : null;
                          const newNiveaux = niveaux.map(n => 
                            n.id === niveau.id ? { 
                              ...n, 
                              pointsMax: value 
                            } : n
                          );
                          setNiveaux(newNiveaux);
                          checkForChanges(newNiveaux);
                        }}
                        className="w-24 px-2 py-1 border rounded"
                        placeholder="∞"
                      />
                      {niveau.pointsMax !== null && niveau.pointsMax <= niveau.pointsMin && (
                        <p className="text-xs text-red-600 mt-1">
                          ⚠️ Doit être supérieur à {niveau.pointsMin}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 1.5: Affichage visuel des niveaux */}
        <div className="mb-8 border-t pt-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">📊 Vue d'ensemble des Niveaux</h2>
            <p className="text-sm text-gray-600 mb-1">
              Les clients sont classés automatiquement selon le nombre de points accumulés.
            </p>
            <p className="text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded-lg mt-3">
              💡 <strong>Comment ça marche ?</strong> Chaque niveau débloque des avantages et un statut privilégié. Plus le client accumule de points, plus il progresse dans les niveaux.
            </p>
          </div>

          <div className="space-y-4">
            {niveaux.map((niveau) => {
              const pointsMin = niveau.pointsMin || 0;
              const pointsMax = niveau.pointsMax === null ? null : niveau.pointsMax;
              const pointsRange = pointsMax === null ? `${pointsMin}+ points` : `${pointsMin} - ${pointsMax} points`;
              
              // Styles par niveau
              const getStyles = (code) => {
                switch (code) {
                  case 'BASIC':
                    return {
                      border: 'border-2 border-gray-300',
                      bg: 'bg-gradient-to-br from-gray-50 to-gray-100',
                      iconBg: 'bg-gray-500',
                      textColor: 'text-gray-700',
                      icon: niveau.icone || '🎯'
                    };
                  case 'BRONZE':
                    return {
                      border: 'border-2 border-amber-300',
                      bg: 'bg-gradient-to-br from-amber-50 to-orange-50',
                      iconBg: 'bg-amber-600',
                      textColor: 'text-amber-700',
                      icon: niveau.icone || '🥉'
                    };
                  case 'ARGENT':
                    return {
                      border: 'border-2 border-gray-400',
                      bg: 'bg-gradient-to-br from-gray-100 to-slate-100',
                      iconBg: 'bg-gray-400',
                      textColor: 'text-gray-700',
                      icon: niveau.icone || '🥈'
                    };
                  case 'OR':
                    return {
                      border: 'border-2 border-yellow-400',
                      bg: 'bg-gradient-to-br from-yellow-50 to-amber-50',
                      iconBg: 'bg-yellow-500',
                      textColor: 'text-yellow-700',
                      icon: niveau.icone || '🥇'
                    };
                  case 'PLATINUM':
                  case 'PLATINE':
                    return {
                      border: 'border-2 border-indigo-300',
                      bg: 'bg-gradient-to-br from-indigo-50 to-purple-50',
                      iconBg: 'bg-indigo-600',
                      textColor: 'text-indigo-700',
                      icon: niveau.icone || '💎'
                    };
                  default:
                    return {
                      border: 'border-2 border-gray-300',
                      bg: 'bg-gradient-to-br from-gray-50 to-gray-100',
                      iconBg: 'bg-gray-500',
                      textColor: 'text-gray-700',
                      icon: niveau.icone || '🎯'
                    };
                }
              };

              const niveauCode = niveau.code || niveau.niveau?.toUpperCase();
              const styles = getStyles(niveauCode);

              return (
                <div key={niveau.id} className={`${styles.border} rounded-xl p-5 ${styles.bg}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-full ${styles.iconBg} flex items-center justify-center text-white font-bold text-xl`}>
                      {styles.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 text-lg">{niveau.nom}</h4>
                      <p className="text-xs text-gray-600">
                        {niveau.description || 'Niveau de fidélité'}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${styles.textColor}`}>
                        {pointsRange}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-600 bg-white/70 rounded px-3 py-2">
                    {niveauCode === 'BASIC' || (niveau.pointsMin || 0) === 0 ? (
                      <strong>Niveau d'entrée :</strong>
                    ) : (
                      <strong>Seuil :</strong>
                    )}{' '}
                    {niveauCode === 'BASIC' || (niveau.pointsMin || 0) === 0 ? (
                      <>Tous les clients commencent au niveau {niveau.nom} dès leur première commande.</>
                    ) : (
                      <>Le client atteint le niveau {niveau.nom} lorsqu'il accumule <strong>{pointsMin} points</strong> ou plus.</>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Note d'information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <div className="text-blue-600 text-lg">ℹ️</div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900 mb-1">Progression automatique</p>
                  <p className="text-xs text-blue-800">
                    Les clients montent automatiquement de niveau lorsque leur nombre de points atteint le seuil requis. Le niveau est calculé en temps réel et visible sur leur carte de fidélité.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Avantages Clickorico Globaux */}
        <div className="mb-8 border-t pt-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold mb-2">🎁 Avantages Clickorico Globaux</h2>
              <p className="text-sm text-gray-600 mb-1">
                Configurez les avantages offerts par Clickorico selon le statut global des clients (Bronze, Argent, Or, Platine, Diamant).
              </p>
              <p className="text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded-lg mt-3">
                💡 <strong>Important :</strong> Ces avantages sont pris en charge par Clickorico et s'appliquent à toutes les boutiques. Ils récompensent les meilleurs clients indépendamment de leur fidélité à une boutique spécifique.
              </p>
            </div>
            <button
              onClick={() => {
                setEditingAvantage(null);
                setAvantageForm({
                  nom: '',
                  description: '',
                  type: 'REDUCTION_POURCENT',
                  actif: false,
                  ordre: 0,
                  valeur: null,
                  statutMinimum: 'BRONZE',
                  dateDebut: null,
                  dateFin: null,
                  prisEnChargeParClickorico: true,
                  coutClickorico: null
                });
                setShowAvantageForm(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Nouvel avantage
            </button>
          </div>

          {showAvantageForm && (
            <div className="mb-6 bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">
                {editingAvantage ? 'Modifier l\'avantage' : 'Créer un nouvel avantage'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nom de l'avantage *</label>
                  <input
                    type="text"
                    value={avantageForm.nom}
                    onChange={(e) => setAvantageForm({ ...avantageForm, nom: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Ex: Livraison gratuite partout"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type d'avantage *</label>
                  <select
                    value={avantageForm.type}
                    onChange={(e) => setAvantageForm({ ...avantageForm, type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="REDUCTION_POURCENT">Réduction %</option>
                    <option value="REDUCTION_MONTANT">Réduction montant</option>
                    <option value="LIVRAISON_GRATUITE">Livraison gratuite</option>
                    <option value="PRODUIT_OFFERT">Produit offert</option>
                    <option value="MENU_OFFERT">Menu offert</option>
                    <option value="BOISSON_OFFERTE">Boisson offerte</option>
                    <option value="DESSERT_OFFERT">Dessert offert</option>
                    <option value="POINTS_BONUS">Points bonus</option>
                    <option value="PRIORITE_LIVRAISON">Priorité livraison</option>
                    <option value="ACCES_EVENEMENT">Accès événement exclusif</option>
                    <option value="AUTRE">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={avantageForm.description}
                    onChange={(e) => setAvantageForm({ ...avantageForm, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={2}
                    placeholder="Description de l'avantage"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Statut minimum requis *</label>
                  <select
                    value={avantageForm.statutMinimum}
                    onChange={(e) => setAvantageForm({ ...avantageForm, statutMinimum: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="BRONZE">Bronze</option>
                    <option value="ARGENT">Argent</option>
                    <option value="OR">Or</option>
                    <option value="PLATINE">Platine</option>
                    <option value="DIAMANT">Diamant</option>
                  </select>
                </div>
                {(avantageForm.type === 'REDUCTION_POURCENT' || avantageForm.type === 'REDUCTION_MONTANT' || avantageForm.type === 'POINTS_BONUS') && (
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {avantageForm.type === 'REDUCTION_POURCENT' ? 'Valeur (%)' : avantageForm.type === 'POINTS_BONUS' ? 'Points bonus' : 'Montant (€)'}
                    </label>
                    <input
                      type="number"
                      step={avantageForm.type === 'REDUCTION_POURCENT' ? 1 : 0.1}
                      value={avantageForm.valeur || ''}
                      onChange={(e) => setAvantageForm({ ...avantageForm, valeur: e.target.value ? parseFloat(e.target.value) : null })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder={avantageForm.type === 'REDUCTION_POURCENT' ? 'Ex: 10' : 'Ex: 5'}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1">Coût Clickorico (€)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={avantageForm.coutClickorico || ''}
                    onChange={(e) => setAvantageForm({ ...avantageForm, coutClickorico: e.target.value ? parseFloat(e.target.value) : null })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Ex: 3.50"
                  />
                  <p className="text-xs text-gray-500 mt-1">Coût estimé pour Clickorico par utilisation</p>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={avantageForm.actif}
                      onChange={(e) => setAvantageForm({ ...avantageForm, actif: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Actif</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={avantageForm.prisEnChargeParClickorico}
                      onChange={(e) => setAvantageForm({ ...avantageForm, prisEnChargeParClickorico: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Pris en charge par Clickorico</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleSaveAvantage}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  {editingAvantage ? 'Mettre à jour' : 'Créer'}
                </button>
                <button
                  onClick={() => {
                    setShowAvantageForm(false);
                    setEditingAvantage(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {loadingAvantages ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : avantagesClickorico.length === 0 ? (
            <div className="text-center p-8 text-gray-500 bg-gray-50 rounded-lg">
              <Gift className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>Aucun avantage Clickorico configuré pour le moment.</p>
              <p className="text-sm mt-2">Créez votre premier avantage pour récompenser vos meilleurs clients !</p>
            </div>
          ) : (
            <div className="space-y-4">
              {['BRONZE', 'ARGENT', 'OR', 'PLATINE', 'DIAMANT'].map((statut) => {
                const avantagesStatut = avantagesClickorico.filter(a => a.statutMinimum === statut);
                if (avantagesStatut.length === 0) return null;

                return (
                  <div key={statut} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="text-2xl">
                        {statut === 'BRONZE' ? '🥉' : statut === 'ARGENT' ? '🥈' : statut === 'OR' ? '🥇' : statut === 'PLATINE' ? '💎' : '👑'}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Statut {statut}</h4>
                        <p className="text-xs text-gray-600">{avantagesStatut.length} avantage(s) disponible(s)</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {avantagesStatut.map((avantage) => (
                        <div key={avantage.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="font-semibold text-gray-900">{avantage.nom}</h5>
                                {avantage.actif ? (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Actif</span>
                                ) : (
                                  <span className="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs rounded-full">Inactif</span>
                                )}
                              </div>
                              {avantage.description && (
                                <p className="text-sm text-gray-600 mb-2">{avantage.description}</p>
                              )}
                              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                                <span>Type: {avantage.type.replace(/_/g, ' ')}</span>
                                {avantage.valeur && (
                                  <span>• Valeur: {avantage.valeur}{avantage.type === 'REDUCTION_POURCENT' ? '%' : '€'}</span>
                                )}
                                {avantage.coutClickorico && (
                                  <span>• Coût: {avantage.coutClickorico}€</span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditAvantage(avantage)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                title="Modifier"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteAvantage(avantage.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Section 3: Service Client */}
        <div className="border-t pt-8">
          <h2 className="text-xl font-semibold mb-4">Service Client - Ajustement des Points</h2>
          <p className="text-sm text-gray-600 mb-4">
            Recherchez un client et ajustez ses points manuellement (ajout ou suppression).
          </p>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Email ou nom du client"
                className="flex-1 px-4 py-2 border rounded-lg"
                onKeyPress={(e) => e.key === 'Enter' && searchClient()}
              />
              <button
                onClick={searchClient}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Search className="w-5 h-5" />
              </button>
            </div>

            {clientResults.length > 0 && (
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Résultats de recherche</h3>
                {clientResults.map((client) => (
                  <div
                    key={client.id}
                    onClick={() => setSelectedClient(client)}
                    className="p-2 hover:bg-gray-50 cursor-pointer rounded"
                  >
                    {client.prenom} {client.nom} ({client.email})
                  </div>
                ))}
              </div>
            )}

            {selectedClient && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-semibold mb-4">Ajuster les points pour {selectedClient.prenom} {selectedClient.nom}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Points à ajuster</label>
                    <input
                      type="number"
                      value={pointsAjustement}
                      onChange={(e) => setPointsAjustement(e.target.value)}
                      placeholder="Ex: 100 (ajout) ou -50 (retrait)"
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Raison de l'ajustement</label>
                    <textarea
                      value={raisonAjustement}
                      onChange={(e) => setRaisonAjustement(e.target.value)}
                      placeholder="Ex: Remise pour problème de livraison"
                      className="w-full px-4 py-2 border rounded-lg"
                      rows={3}
                    />
                  </div>
                  <button
                    onClick={ajusterPoints}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Save className="w-5 h-5 inline mr-2" />
                    Appliquer l'ajustement
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Fidelite;

